import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IButtonType } from 'src/app/interfaces/components/button.interface';
import { ContentAreaComponent } from "src/app/components/content-area/content-area.component";
import { FormAreaComponent } from "src/app/components/form-area/form-area.component";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputComponent } from "src/app/components/input/input.component";
import { SelectComponent } from "src/app/components/select/select.component";
import { CheckboxComponent } from "src/app/components/checkbox/checkbox.component";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { matrixEnterpriseOptions } from './clients-form.constants';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { ClientsService } from 'src/app/services/clients.service';
import { AlertService } from 'src/app/services/alert.service';
import { ISelectOption, ISelectOptionApi } from 'src/app/interfaces/components/select.interface';
import { DocumentUploadComponent } from "src/app/components/document-upload/document-upload.component";

@Component({
  selector: 'app-clients-form',
  standalone: true,
  imports: [
    ContentAreaComponent,
    FormAreaComponent,
    ReactiveFormsModule,
    TranslateModule,
    InputComponent,
    SelectComponent,
    CheckboxComponent,
    MatSlideToggle,
    DocumentUploadComponent
],
  templateUrl: './clients-form.component.html'
})
export class ClientsFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(Router);
  private readonly router = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly clientsService = inject(ClientsService);
  private readonly alertService = inject(AlertService);

  matrixEnterpriseOptions = matrixEnterpriseOptions;

  id = '';
  title = '';
  enterpriseList: ISelectOption[] = [];

  form: FormGroup = this.fb.group({
    id: [null],
    name: [null, Validators.required],
    login: [null],
    status: [true],
    personData: this.fb.group({
      id: [null],
      document: ['', Validators.required],
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      isHeadOffice: [true, Validators.required],
      isBrf : [false, Validators.required],
      sapBukrs: [''],
      sapBranch: [''],
      sapWerks: [''],
      headOfficeId: [null],
    }),
    plansData: this.fb.group({
      pdvQuantity: ['', Validators.required],
      monthlyFee: ['', Validators.required],
      observation: [''],
      contractFileUrl: [null],
    }),
  });

  ngOnInit(): void {
    this.id = this.router.snapshot.params['id'] ?? '';
    this.title = this.id ? 'CLIENTS_FORM.EDIT_TITLE' : 'CLIENTS_FORM.ADD_TITLE';
    this.getEnterpriseList();

    if (this.id) {
      this.form.get('id')?.setValue(this.id);
      this.form.get('personData.isHeadOffice')?.disable();
      this.form.get('personData.headOfficeId')?.disable();
      this.initialFormValue();
    }
  }

  initialFormValue() {
    const id = this.form.get('id')?.value;

    this.clientsService.getById(id).subscribe((res) => {
      this.form.patchValue({
        ...res.data,
        personData: res.data.personData[0],
        plansData: res.data.plansData[0],
      });
    });
  }

  getEnterpriseList(event?: string) {
    this.clientsService.headOfficeList({
      name: event,
    }).subscribe((res) => {
      const data = res.data.map((item: ISelectOptionApi) => {
        return {
          value: item.id,
          label: item.name,
        };
      });

      this.enterpriseList = data.filter((item: ISelectOption) => item.value);
    });
  }

  handleEmailChange(event: string) {
    if (this.form.get('personData.isHeadOffice')?.value === false) {
      this.form.get('login')?.setValue(event);
    }
  }

  matrixEnterpriseChange() {
    const isHeadOffice = this.form.get('personData.isHeadOffice')?.value;
    const headOffice = this.form.get('personData.headOfficeId');
    const name = this.form.get('name');
    const login = this.form.get('login');

    if (isHeadOffice === true) {
      name?.setValidators([Validators.required]);
      login?.setValidators([Validators.required]);

      headOffice?.reset();
      headOffice?.clearValidators();
    }
    else {
      headOffice?.setValidators([Validators.required]);

      login?.reset();
      name?.reset();
      login?.clearValidators();
      name?.clearValidators();
    }

    name?.updateValueAndValidity();
    login?.updateValueAndValidity();
    headOffice?.updateValueAndValidity();

    this.sapChange();
  }

  sapChange() {
    const isBrf = this.form.get('personData.isBrf')?.value;
    const isHeadOffice = this.form.get('personData.isHeadOffice')?.value;

    const sapBukrs = this.form.get('personData.sapBukrs');
    const sapBranch = this.form.get('personData.sapBranch');
    const sapWerks = this.form.get('personData.sapWerks');

    sapBukrs?.clearValidators();
    sapBranch?.clearValidators();
    sapWerks?.clearValidators();

    if (isBrf) {
      sapWerks?.setValidators([Validators.required]);

      if (isHeadOffice) {
        sapBukrs?.setValidators([Validators.required]);

        sapBranch?.reset();
      } else {
        sapBranch?.setValidators([Validators.required]);

        sapBukrs?.reset();
      }
    } else {
      sapBukrs?.reset();
      sapBranch?.reset();
      sapWerks?.reset();
    }

    sapBukrs?.updateValueAndValidity();
    sapBranch?.updateValueAndValidity();
    sapWerks?.updateValueAndValidity();
  }

  back(): void {
    this.route.navigate(['/panel-master/clients']);
  }

  handleFormAreaEvent(event: IButtonType) {
    if (event === 'save') {
      this.submitForm();
    }
    if (event === 'cancel') {
      const subtitle = this.form.get('id')?.value === null
        ? 'CLIENTS_FORM.MODAL.CANCEL_ADD'
        : 'CLIENTS_FORM.MODAL.CANCEL_EDIT';

      if (this.form.touched) {
        this.dialog.open(ModalComponent, {
          data: {
            type: 'cancel',
            subtitle: this.translate.instant(subtitle),
          },
        })
        .afterClosed().subscribe((confirm) => {
          if (confirm) {
            this.back();
          }
        });
      } else {
        this.back();
      }
    }
  }

  submitForm() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.show({
        type: 'alert',
      });
    }
    else {
      this.clientsService.register(this.form.getRawValue()).subscribe({
        next: () => {
          this.back();
        },
        error: (error) => {
          this.alertService.show({
            type: 'error',
            subtitle: error?.error?.message || ''
          });
        }
      });
    }
  }
}
